import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Tooltip } from "@material-ui/core";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import Pagination from "@material-ui/lab/Pagination";
import { useSnackbar } from "notistack";
import theme from "../../theme";
import CommonTable from "../table";
import BankCodeEditDrawer from "./BankCodeEditDrawer";
import {
  PAGE_SIZE,
  toMessages,
  formatDateTime,
  readResolutionRows,
  readResolutionCount,
} from "./utils";
import {
  getFortressBankCodesAction,
  getFortressBankCodesErrorAction,
  updateFortressBankCodeAction,
  updateFortressBankCodeSuccessAction,
  updateFortressBankCodeErrorAction,
} from "../../containers/dashboard/action";

const EMPTY_FILTERS = {
  search: "",
  bank_name: "",
  bank_code: "",
  institution_code: "",
};

const FILTER_LABELS = {
  search: "Search",
  bank_name: "Bank name",
  bank_code: "Bank code",
  institution_code: "Institution code",
};

const useStyles = makeStyles(() => ({
  toolbar: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    alignItems: "flex-end",
    marginBottom: "16px",
  },
  field: { display: "flex", flexDirection: "column" },
  fieldLabel: {
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    color: theme.colors.Violet,
    opacity: 0.7,
    marginBottom: "5px",
  },
  input: {
    boxSizing: "border-box",
    padding: "9px 12px",
    fontSize: "14px",
    fontFamily: theme.fontFamily.MARKPRO,
    color: theme.colors.Violet,
    backgroundColor: theme.colors.Zumthor,
    border: `1px solid ${theme.colors.Zumthor}`,
    borderRadius: "6px",
    outline: "none",
    "&:focus": { border: `1px solid ${theme.colors.DodgerBlue}` },
  },
  searchInput: { width: "260px", maxWidth: "100%" },
  filterInput: { width: "170px", maxWidth: "100%" },
  chipsRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
    marginBottom: "14px",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    backgroundColor: theme.colors.Zumthor,
    color: theme.colors.Violet,
    borderRadius: "999px",
    padding: "3px 10px",
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
  },
  chipX: { cursor: "pointer", fontFamily: theme.fontFamily.MARKPROBOLD, color: theme.colors.DodgerBlue },
  clearAll: {
    cursor: "pointer",
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.RejectRed,
  },
  count: {
    fontSize: "13px",
    fontFamily: theme.fontFamily.MARKPROBOOK,
    color: theme.colors.Violet,
    opacity: 0.75,
    marginTop: "18px",
  },
  actionsCell: { display: "flex", gap: "2px", justifyContent: "flex-end" },
  code: {
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    color: theme.colors.Violet,
  },
}));

function BankCodesTable() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [editTarget, setEditTarget] = useState(null);

  const list = useSelector((s) => s.dashboardReducer?.fortressBankCodes);
  const loading = useSelector((s) => !!s.dashboardReducer?.fortressBankCodesLoading);
  const listError = useSelector((s) => s.dashboardReducer?.fortressBankCodesError);
  const mutationLoading = useSelector(
    (s) => !!s.dashboardReducer?.updateFortressBankCodeLoading
  );
  const updateSuccess = useSelector(
    (s) => s.dashboardReducer?.updateFortressBankCodeSuccess
  );
  const updateError = useSelector(
    (s) => s.dashboardReducer?.updateFortressBankCodeError
  );
  const fieldErrors = useSelector(
    (s) => s.dashboardReducer?.fortressBankCodeFieldErrors
  );

  const rows = readResolutionRows(list);
  const count = readResolutionCount(list);
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const buildParams = useCallback(
    (p) => {
      const params = { page: p, page_size: PAGE_SIZE };
      Object.keys(filters).forEach((k) => {
        if (filters[k] && filters[k].trim()) params[k] = filters[k].trim();
      });
      return params;
    },
    [filters]
  );

  // Debounced fetch on filter/page change (filters reset page to 1 below).
  useEffect(() => {
    const handle = setTimeout(() => {
      dispatch(getFortressBankCodesAction(buildParams(page)));
    }, 300);
    return () => clearTimeout(handle);
  }, [dispatch, buildParams, page]);

  useEffect(() => {
    if (!listError) return;
    toMessages(listError, "Unable to load bank-code resolutions.").forEach((m) =>
      enqueueSnackbar(m, { variant: "error" })
    );
    dispatch(getFortressBankCodesErrorAction(null));
  }, [listError, enqueueSnackbar, dispatch]);

  useEffect(() => {
    if (!updateSuccess) return;
    enqueueSnackbar("Bank code updated.", { variant: "success" });
    dispatch(updateFortressBankCodeSuccessAction(null));
    setEditTarget(null);
    dispatch(getFortressBankCodesAction(buildParams(page)));
  }, [updateSuccess, enqueueSnackbar, dispatch, buildParams, page]);

  useEffect(() => {
    if (!updateError) return;
    toMessages(updateError, "Unable to update bank code.").forEach((m) =>
      enqueueSnackbar(m, { variant: "error" })
    );
    // Keep the drawer open so field errors show; just clear the top-level error.
    dispatch(updateFortressBankCodeErrorAction(null, fieldErrors));
  }, [updateError, enqueueSnackbar, dispatch, fieldErrors]);

  const setFilter = (key, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };
  const clearFilter = (key) => setFilter(key, "");
  const clearAll = () => {
    setPage(1);
    setFilters(EMPTY_FILTERS);
  };

  const activeFilters = Object.keys(filters).filter(
    (k) => filters[k] && filters[k].trim()
  );

  const columns = useMemo(
    () => [
      { Header: "Bank Name", accessor: "bank_name", Cell: (v) => v || "—" },
      {
        Header: "Bank Code",
        accessor: "bank_code",
        Cell: (v) => <span className={classes.code}>{v || "—"}</span>,
      },
      {
        Header: "Institution Code",
        accessor: "institution_code",
        Cell: (v) => <span className={classes.code}>{v || "—"}</span>,
      },
      {
        Header: "Updated",
        accessor: "updated_at",
        Cell: (v) => formatDateTime(v),
      },
      {
        Header: "",
        accessor: "id",
        Cell: (_v, row) => (
          <div className={classes.actionsCell}>
            <Tooltip title="Edit bank code">
              <IconButton
                size="small"
                aria-label={`Edit ${row.bank_name}`}
                onClick={() => setEditTarget(row)}
              >
                <EditOutlinedIcon fontSize="small" style={{ color: theme.colors.DodgerBlue }} />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    [classes] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div>
      <div className={classes.toolbar}>
        <div className={classes.field}>
          <span className={classes.fieldLabel}>Search</span>
          <input
            className={`${classes.input} ${classes.searchInput}`}
            placeholder="Bank name, bank code or institution code"
            value={filters.search}
            onChange={(e) => setFilter("search", e.target.value)}
            aria-label="Search resolutions"
          />
        </div>
        <div className={classes.field}>
          <span className={classes.fieldLabel}>Bank name</span>
          <input
            className={`${classes.input} ${classes.filterInput}`}
            placeholder="Exact"
            value={filters.bank_name}
            onChange={(e) => setFilter("bank_name", e.target.value)}
          />
        </div>
        <div className={classes.field}>
          <span className={classes.fieldLabel}>Bank code</span>
          <input
            className={`${classes.input} ${classes.filterInput}`}
            placeholder="Exact"
            value={filters.bank_code}
            onChange={(e) => setFilter("bank_code", e.target.value)}
          />
        </div>
        <div className={classes.field}>
          <span className={classes.fieldLabel}>Institution code</span>
          <input
            className={`${classes.input} ${classes.filterInput}`}
            placeholder="Exact"
            value={filters.institution_code}
            onChange={(e) => setFilter("institution_code", e.target.value)}
          />
        </div>
      </div>

      {activeFilters.length ? (
        <div className={classes.chipsRow}>
          {activeFilters.map((k) => (
            <span key={k} className={classes.chip}>
              {FILTER_LABELS[k]}: {filters[k]}
              <span className={classes.chipX} onClick={() => clearFilter(k)}>
                ×
              </span>
            </span>
          ))}
          <span className={classes.clearAll} onClick={clearAll}>
            Clear all
          </span>
        </div>
      ) : null}

      <CommonTable columns={columns} data={rows} loading={loading} fullRadius />

      {count > 0 ? (
        <>
          <div className={classes.count}>
            {count} resolution{count === 1 ? "" : "s"}
          </div>
          <div className={"pagination"}>
            <Pagination
              page={page}
              count={totalPages}
              showFirstButton
              showLastButton
              onChange={(_, value) => setPage(value)}
            />
          </div>
        </>
      ) : null}

      <BankCodeEditDrawer
        open={!!editTarget}
        resolution={editTarget}
        loading={mutationLoading}
        fieldErrors={fieldErrors}
        onSubmit={(body) =>
          editTarget && dispatch(updateFortressBankCodeAction(body, editTarget.id))
        }
        onClose={() => {
          if (mutationLoading) return;
          setEditTarget(null);
          dispatch(updateFortressBankCodeErrorAction(null));
        }}
      />
    </div>
  );
}

export default BankCodesTable;
